import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Body2,
  Input,
  Label,
  EyeOffIcon,
  EyeIcon,
  Dialog,
  Button,
  ArrowRightIcon,
  SpinnerLoader,
} from "@activecollab/components";
import PropTypes from "prop-types";
import { showErrorToast, showSuccessToast } from "~/components/Toast";
import { fetchAccountSettings } from "~/store/actions/actions.accountSettings";
import { verifyPassword } from "~/store/actions/actions.cancelSubscription";
import { getCancellationRequestObjectId } from "~/store/selectors/selector.cancellationRequestObject";

const CancellationProcessSecondStep = ({ onClick }) => {
  const navigate = useNavigate();
  const cancellationRequestObjectId = useSelector(getCancellationRequestObjectId);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  let inputType = showPassword ? "text" : "password";

  const onCancelClick = useCallback(() => {
    setIsLoading(true);
    dispatch(verifyPassword(password, cancellationRequestObjectId))
      .then((response) => {
        if (response.response.is_verified) {
          setIsLoading(false);
          if (onClick) {
            onClick();
          }
          dispatch(fetchAccountSettings());
          showSuccessToast("Account successfully canceled.");
          navigate(0);
        } else {
          setIsLoading(false);
          showErrorToast("The password you entered is incorrect");
        }
      })
      .catch(() => {
        setIsLoading(false);
        showErrorToast("Oops! Something went wrong");
      });
  }, [cancellationRequestObjectId, dispatch, navigate, onClick, password]);

  const toggleShowPassword = useCallback(() => setShowPassword(!showPassword), [showPassword]);

  const showPasswordToggler = useMemo(() => {
    return (
      <div
        onClick={toggleShowPassword}
        className="tw-cursor-pointer tw-mr-2 tw-mt-1"
      >
        {showPassword ? <EyeIcon /> : <EyeOffIcon />}
      </div>
    );
  }, [showPassword, toggleShowPassword]);

  const onInputChange = useCallback((e) => {
    setPassword(e.target.value);
  }, []);

  return (
    <>
      <Dialog.Content>
        <Body2
          color="tertiary"
          className="tw-mb-6"
        >
          Before you cancel your subscription, we need to make sure this is really you.
        </Body2>
        <Label
          htmlFor="password"
          className="tw-mb-1"
          weight="bold"
        >
          Password:
        </Label>
        <Input
          type={inputType}
          id="password"
          required
          className="tw-w-[300px]"
          autoFocus
          size="big"
          endAdornment={showPasswordToggler}
          value={password}
          onChange={onInputChange}
        />
      </Dialog.Content>
      <Dialog.ContentDivider />
      <Dialog.Actions>
        {isLoading ? (
          <div
            className="tw-flex"
            data-testid="spinnerDiv"
          >
            <SpinnerLoader />
            <span className="tw-ml-2">Processing</span>
          </div>
        ) : (
          <Button
            type="submit"
            form="billing_address_form"
            disabled={password.length < 1 && !isLoading}
            onClick={onCancelClick}
          >
            Cancel Subscription
            <ArrowRightIcon />
          </Button>
        )}
      </Dialog.Actions>
    </>
  );
};

CancellationProcessSecondStep.propTypes = {
  onClick: PropTypes.func,
};

export default CancellationProcessSecondStep;
